/*
  Vitals — booking checkout hand-off (web).

  A blood-test booking is a throwaway, single-line cart: the customer configures one appointment and
  goes straight to payment. Two ways to get them there:

    1. Storefront API `cartCreate` (preferred, needs `settings.storefront_token`). Creates the cart
       WITH a buyer identity — email, phone, and the delivery address — so Shopify's checkout arrives
       pre-filled instead of asking the customer to retype an address we already know. This mirrors
       what the mobile app does in `useCreateBookingCart`.
    2. The AJAX cart (`/cart/clear.js` + `/cart/add.js` → `/checkout`), which is what the booking
       forms did before and which carries no address. Used whenever no token is configured, or when
       anything about (1) fails — the booking must always be completable.

  Line-item `properties` (AJAX) and line `attributes` (Storefront) are the same key/value pairs, so
  the resulting order looks identical either way.

  Usage:
    VitalsBookingCheckout.go({
      domain: 'shop.myshopify.com', token: '...', version: '2024-01',
      items: [{ id: 123, quantity: 1, properties: { ... } }],
      buyer: { email: '', phone: '', address: { address1, city, province, zip, firstName, lastName } },
      discountCodes: ['BULK3'],   // optional
      attributes: { _installment: 'yes' },   // optional cart attributes
      ajaxFallback: false,        // optional: reject instead of replacing the customer's AJAX cart
      cartAddUrl: '/cart/add.js', cartClearUrl: '/cart/clear.js'
    });   // → resolves after navigation starts, rejects only if BOTH paths fail
*/
(function () {
  'use strict';

  if (window.VitalsBookingCheckout) return;

  var CART_CREATE = 'mutation VitalsBookingCartCreate($input: CartInput!) {' +
    ' cartCreate(input: $input) {' +
    '   cart { id checkoutUrl }' +
    '   userErrors { field message }' +
    ' }' +
    '}';

  function trimmed(value) {
    var out = (value == null ? '' : String(value)).trim();
    return out.length ? out : null;
  }

  /** Drop empty keys — Shopify rejects a `MailingAddressInput` full of empty strings. */
  function compactAddress(address) {
    if (!address) return null;
    var out = {};
    Object.keys(address).forEach(function (key) {
      var value = trimmed(address[key]);
      if (value) out[key] = value;
    });
    return Object.keys(out).length ? out : null;
  }

  function discountCodes(opts) {
    return (opts.discountCodes || []).map(trimmed).filter(Boolean);
  }

  /** AJAX cart path: clear (so re-submits don't stack lines), add, then the normal checkout URL. */
  function ajaxCheckout(opts) {
    var clearUrl = opts.cartClearUrl || '/cart/clear.js';
    var addUrl = opts.cartAddUrl || '/cart/add.js';
    return fetch(clearUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' } })
      .then(function () {
        return fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: opts.items })
        });
      })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (err) {
            throw new Error((err && err.description) || 'Could not add to cart.');
          });
        }
        var codes = discountCodes(opts);
        window.location.href = '/checkout' + (codes.length ? '?discount=' + encodeURIComponent(codes.join(',')) : '');
      });
  }

  function storefrontRequest(opts, input) {
    var url = 'https://' + opts.domain + '/api/' + (opts.version || '2024-01') + '/graphql.json';
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Shopify-Storefront-Access-Token': opts.token
      },
      body: JSON.stringify({ query: CART_CREATE, variables: { input: input } })
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (json) {
        var payload = json && json.data && json.data.cartCreate;
        if (!payload) return null;
        if (payload.userErrors && payload.userErrors.length) return null;
        return payload.cart && payload.cart.checkoutUrl ? payload.cart.checkoutUrl : null;
      })
      .catch(function () { return null; });
  }

  /**
   * Create the booking cart with a pre-filled buyer identity and return its checkout URL. Tried in
   * three passes — full identity, contact only, no identity — so a delivery address Shopify dislikes
   * costs us the address prefill but never the booking itself.
   */
  function storefrontCheckoutUrl(opts) {
    var buyer = opts.buyer || {};
    var contact = {};
    var email = trimmed(buyer.email);
    var phone = trimmed(buyer.phone);
    if (email) contact.email = email;
    if (phone) contact.phone = phone;
    if (Object.keys(contact).length) contact.countryCode = 'TH';

    var address = compactAddress(buyer.address);
    if (address && !address.country) address.country = 'Thailand';

    var lines = opts.items.map(function (item) {
      var props = item.properties || {};
      return {
        merchandiseId: 'gid://shopify/ProductVariant/' + item.id,
        quantity: item.quantity || 1,
        attributes: Object.keys(props).map(function (key) {
          return { key: key, value: String(props[key] == null ? '' : props[key]) };
        })
      };
    });

    var identities = [];
    if (address) {
      var full = {};
      Object.keys(contact).forEach(function (k) { full[k] = contact[k]; });
      full.deliveryAddressPreferences = [{ deliveryAddress: address }];
      identities.push(full);
    }
    if (Object.keys(contact).length) identities.push(contact);
    identities.push(null);

    var codes = discountCodes(opts);
    var attrs = Object.keys(opts.attributes || {}).map(function (key) {
      return { key: key, value: String(opts.attributes[key] == null ? '' : opts.attributes[key]) };
    });

    // Sequential fallback: stop at the first identity Shopify accepts.
    return identities.reduce(function (chain, identity) {
      return chain.then(function (url) {
        if (url) return url;
        var input = { lines: lines };
        if (codes.length) input.discountCodes = codes;
        if (attrs.length) input.attributes = attrs;
        if (identity) input.buyerIdentity = identity;
        return storefrontRequest(opts, input);
      });
    }, Promise.resolve(null));
  }

  /**
   * Send the customer to checkout for this booking. Uses the Storefront path when a token is
   * configured and falls back to the AJAX cart on any failure, so the only way this rejects is if
   * the AJAX cart itself refuses the line.
   */
  function go(opts) {
    if (!opts || !opts.items || !opts.items.length) {
      return Promise.reject(new Error('No booking line to check out.'));
    }
    // The AJAX path clears the cart first, so callers buying alongside a standing cart opt out.
    var fallback = opts.ajaxFallback === false
      ? function () { return Promise.reject(new Error('Could not create checkout.')); }
      : function () { return ajaxCheckout(opts); };
    if (!opts.token || !opts.domain) return fallback();

    return storefrontCheckoutUrl(opts)
      .then(function (checkoutUrl) {
        if (!checkoutUrl) return fallback();
        window.location.href = checkoutUrl;
      }, fallback);
  }

  window.VitalsBookingCheckout = { go: go };
})();
